import { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { getPrayerTimes, API } from "../lib/api";
import { useLocation } from "../hooks/useLocation";
import { Sun, MapPin, LocateFixed, Search, Loader2, AlertCircle } from "lucide-react";

const NAMES = ["Fajr", "Sunrise", "Dhuhr", "Asr", "Maghrib", "Isha"];

export default function Prayer() {
  const { t } = useApp();
  const { location, setLocation, detect, requesting, error: locError } = useLocation();
  const [timings, setTimings] = useState(null);
  const [meta, setMeta] = useState(null);
  const [now, setNow] = useState(new Date());
  const [query, setQuery] = useState("");
  const [sugg, setSugg] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!location) return;
    getPrayerTimes(location.lat, location.lon)
      .then(d => { setTimings(d?.timings); setMeta(d); })
      .catch(() => {});
  }, [location]);

  useEffect(() => {
    if (query.trim().length < 3) { setSugg([]); return; }
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/geocode`, { params: { q: query }, timeout: 12000 });
        setSugg(data || []);
      } catch { setSugg([]); }
      finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(id);
  }, [query]);

  const pickCity = (s) => {
    setLocation({ lat: s.lat, lon: s.lon, label: s.label });
    setSugg([]);
    setQuery("");
  };

  const upcoming = (() => {
    if (!timings) return null;
    const order = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
    const today = new Date(); today.setHours(0,0,0,0);
    for (const name of order) {
      const [h, m] = (timings[name] || "00:00").split(":").map(Number);
      const d = new Date(today); d.setHours(h, m, 0, 0);
      if (d > now) return { name, date: d };
    }
    const [h, m] = (timings["Fajr"] || "00:00").split(":").map(Number);
    const d = new Date(today); d.setDate(d.getDate() + 1); d.setHours(h, m, 0, 0);
    return { name: "Fajr", date: d };
  })();

  return (
    <div className="space-y-6 fade-in" data-testid="prayer-page">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-nour-primary text-white flex items-center justify-center"><Sun className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t.prayer}</h1>
          <p className="text-sm text-nour-text2 inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" />
            {location?.label || "—"}
            {meta?.meta?.timezone ? ` · ${meta.meta.timezone}` : ""}
          </p>
        </div>
      </header>

      {/* Location selector */}
      <section className="nour-card p-5 space-y-3" data-testid="location-selector">
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-primary" onClick={() => detect()} disabled={requesting} data-testid="prayer-locate">
            {requesting ? <><Loader2 className="w-4 h-4 animate-spin"/> Localisation…</> : <><LocateFixed className="w-4 h-4"/> Me localiser</>}
          </button>
          {location?.source && (
            <span className={`chip ${location.source === "manual" ? "chip-gold" : ""}`}>
              {location.source === "geo" ? "Position GPS" : location.source === "manual" ? "Choix manuel" : "Position"}
            </span>
          )}
        </div>

        <div className="relative">
          <div className="flex items-center gap-2 bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-nour-text2 shrink-0"/>
            <input
              data-testid="prayer-city-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ou cherchez votre ville (ex : Toulon, Paris, Casablanca…)"
              className="flex-1 bg-transparent outline-none text-sm py-1"
            />
            {searching && <Loader2 className="w-4 h-4 animate-spin text-nour-text2"/>}
          </div>
          {sugg.length > 0 && (
            <div className="absolute z-20 mt-1 left-0 right-0 nour-card p-1 max-h-64 overflow-y-auto" data-testid="prayer-city-suggestions">
              {sugg.map((s, i) => (
                <button key={i} onClick={() => pickCity(s)} data-testid={`prayer-city-${i}`} className="w-full text-left px-3 py-2 rounded-lg hover:bg-emerald-50 text-sm flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-nour-primary mt-0.5 shrink-0"/>
                  <span className="line-clamp-2">{s.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {locError && (
          <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="prayer-location-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
            <span>{locError}</span>
          </div>
        )}
      </section>

      {upcoming && (
        <div className="nour-card p-6 bg-gradient-to-br from-emerald-50 to-emerald-100" data-testid="prayer-next">
          <div className="text-xs text-nour-text2 uppercase tracking-wider">{t.next_prayer}</div>
          <div className="font-heading text-3xl font-extrabold text-nour-primary mt-1">{upcoming.name}</div>
          <div className="text-lg tabular-nums mt-1">{upcoming.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="prayer-list">
        {timings && NAMES.map(name => (
          <div
            key={name}
            data-testid={`prayer-${name.toLowerCase()}`}
            className={`nour-card p-5 flex items-center justify-between ${upcoming?.name === name ? "ring-2 ring-nour-primary" : ""}`}
          >
            <div>
              <div className="font-heading font-semibold text-lg">{name}</div>
              <div className="text-xs text-nour-text2">{name === "Sunrise" ? "Shuruq" : ""}</div>
            </div>
            <div className="text-xl tabular-nums text-nour-primary font-semibold">{timings[name]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
